import React, { useState, useEffect } from 'react';
import { X, FileText, Calendar, Download, ChevronRight } from 'lucide-react';
import Button from './Button/Button';

/**
 * @typedef {'csv' | 'xlsx' | 'pdf'} ExportFileFormat
 * @typedef {'currentPage' | 'filtered' | 'dateRange' | 'all'} ExportScope
 */

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} [props.onExportCurrentPage]
 * @param {function} [props.onExportFiltered]
 * @param {function} [props.onExportRange] — (startDate, endDate); legacy immediate download
 * @param {function} [props.onExportAll]
 * @param {boolean} props.loading
 * @param {string} props.title
 * @param {boolean} props.showDateRange
 * @param {Object} [props.scopedFormatExport] — Two-step flow: pick scope (original card UI), then CSV/Excel/PDF at bottom.
 * @param {(scope: ExportScope, format: ExportFileFormat, meta: { startDate?: string, endDate?: string }) => void | Promise<void>} props.scopedFormatExport.onExport
 * @param {ExportFileFormat[]} [props.scopedFormatExport.formats] — default ['csv','xlsx','pdf']
 * @param {boolean} [props.scopedFormatExport.showCurrentPage]
 * @param {boolean} [props.scopedFormatExport.showFiltered]
 * @param {boolean} [props.scopedFormatExport.showDateRange] — overrides props.showDateRange when false
 * @param {boolean} [props.scopedFormatExport.showAll]
 * @param {boolean} [props.scopedFormatExport.dateRangeAllowFutureEnd] — Same as dateRangeAllowFutureEnd for scoped flow only.
 * @param {boolean} [props.dateRangeAllowFutureEnd] — When true, export date inputs have no max=today (use for expected-close / future ranges).
 */
const ExportModal = ({
    isOpen,
    onClose,
    onExportCurrentPage,
    onExportFiltered,
    onExportRange,
    onExportAll,
    loading = false,
    title = "Export Data",
    showDateRange = true,
    scopedFormatExport = null,
    dateRangeAllowFutureEnd = false,
}) => {
    const [exportStartDate, setExportStartDate] = useState("");
    const [exportEndDate, setExportEndDate] = useState("");
    const [selectedScope, setSelectedScope] = useState(
        /** @type {ExportScope | null} */ (null)
    );

    const scoped = scopedFormatExport && typeof scopedFormatExport.onExport === 'function';
    const allowFutureExportDates =
        dateRangeAllowFutureEnd ||
        (scoped && scopedFormatExport.dateRangeAllowFutureEnd === true);
    const formatList =
        scoped && Array.isArray(scopedFormatExport.formats) && scopedFormatExport.formats.length > 0
            ? scopedFormatExport.formats
            : ['csv', 'xlsx', 'pdf'];

    const showScopeCurrent =
        !scoped || scopedFormatExport.showCurrentPage !== false;
    const showScopeFiltered =
        !scoped || scopedFormatExport.showFiltered !== false;
    const showScopeDateRange = scoped
        ? scopedFormatExport.showDateRange !== false && showDateRange
        : showDateRange && !!onExportRange;
    const showScopeAll = !scoped || scopedFormatExport.showAll !== false;

    useEffect(() => {
        if (!isOpen) {
            setExportStartDate("");
            setExportEndDate("");
            setSelectedScope(null);
        }
    }, [isOpen]);

    const clearDates = () => {
        setExportStartDate("");
        setExportEndDate("");
    };

    const handleLegacyExportRange = () => {
        onExportRange(exportStartDate, exportEndDate);
        clearDates();
    };

    const scopeSelected = (id) => selectedScope === id;

    const formatButtonsDisabled =
        loading ||
        !selectedScope ||
        (selectedScope === 'dateRange' && (!exportStartDate || !exportEndDate));

    const handleFormatClick = async (format) => {
        if (!scoped || formatButtonsDisabled) return;
        await scopedFormatExport.onExport(selectedScope, format, {
            startDate: exportStartDate,
            endDate: exportEndDate,
        });
    };

    const formatButtonLabel = (fmt) => {
        if (fmt === 'xlsx') return 'Excel';
        if (fmt === 'pdf') return 'PDF';
        return 'CSV';
    };

    const formatButtonClass = (fmt) => {
        const base =
            "w-full py-2.5 text-sm font-semibold rounded-lg border-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed uppercase tracking-wide";
        if (fmt === 'csv') {
            return `${base} border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 hover:border-emerald-400`;
        }
        if (fmt === 'xlsx') {
            return `${base} border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:border-blue-400`;
        }
        return `${base} border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 hover:border-rose-400`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl transform transition-all m-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="relative space-y-4">
                    {loading && (
                        <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center rounded-xl backdrop-blur-[1px] min-h-[120px]">
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-agrosaf-green border-t-transparent shadow-md"></div>
                            <p className="mt-4 text-sm font-semibold text-agrosaf-green animate-pulse">Preparing your export...</p>
                        </div>
                    )}

                    {/* ——— Scoped format flow: select scope (original card look), then formats at bottom ——— */}
                    {scoped && showScopeCurrent && (
                        <button
                            type="button"
                            onClick={() => setSelectedScope('currentPage')}
                            disabled={loading}
                            className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all group disabled:opacity-50 text-left ${
                                scopeSelected('currentPage')
                                    ? 'border-agrosaf-green bg-green-50 ring-2 ring-agrosaf-green/25'
                                    : 'border-gray-300 hover:border-agrosaf-green hover:bg-green-50'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200">
                                    <FileText className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Export Current Page</p>
                                    <p className="text-xs text-gray-500">Only data visible on this page</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-green shrink-0" />
                        </button>
                    )}

                    {scoped && showScopeFiltered && (
                        <button
                            type="button"
                            onClick={() => setSelectedScope('filtered')}
                            disabled={loading}
                            className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all group disabled:opacity-50 text-left ${
                                scopeSelected('filtered')
                                    ? 'border-agrosaf-blue bg-blue-50 ring-2 ring-blue-200'
                                    : 'border-gray-300 hover:border-agrosaf-blue hover:bg-blue-50'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200">
                                    <Download className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Export Filtered</p>
                                    <p className="text-xs text-gray-500">All data matching current filters</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-blue shrink-0" />
                        </button>
                    )}

                    {scoped && showScopeDateRange && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedScope('dateRange')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setSelectedScope('dateRange');
                                }
                            }}
                            className={`p-4 border-2 rounded-xl space-y-4 cursor-pointer transition-all outline-none ${
                                scopeSelected('dateRange')
                                    ? 'border-orange-500 bg-orange-50/60 ring-2 ring-orange-200'
                                    : 'border-gray-300 hover:border-orange-400'
                            }`}
                        >
                            <div className="flex items-center gap-4 pointer-events-none">
                                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                                    <Calendar className="h-5 w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Export Date Range</p>
                                    <p className="text-xs text-gray-500">Select range carefully</p>
                                </div>
                            </div>

                            <div
                                className="grid grid-cols-2 gap-3 pointer-events-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1 ml-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={exportStartDate}
                                        onChange={(e) => {
                                            setExportStartDate(e.target.value);
                                            setSelectedScope('dateRange');
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.target.showPicker?.();
                                        }}
                                        max={
                                            allowFutureExportDates
                                                ? undefined
                                                : new Date().toISOString().split("T")[0]
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1 ml-1">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={exportEndDate}
                                        onChange={(e) => {
                                            setExportEndDate(e.target.value);
                                            setSelectedScope('dateRange');
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.target.showPicker?.();
                                        }}
                                        min={exportStartDate}
                                        max={
                                            allowFutureExportDates
                                                ? undefined
                                                : new Date().toISOString().split("T")[0]
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {scoped && showScopeAll && (
                        <button
                            type="button"
                            onClick={() => setSelectedScope('all')}
                            disabled={loading}
                            className={`w-full flex items-center justify-between p-4 border-2 rounded-xl transition-all group disabled:opacity-50 text-left ${
                                scopeSelected('all')
                                    ? 'border-agrosaf-green bg-green-50 ring-2 ring-agrosaf-green/25'
                                    : 'border-gray-300 hover:border-agrosaf-green hover:bg-green-50'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-green-100 text-green-600 rounded-lg group-hover:bg-green-200">
                                    <Download className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-900">Export All Data</p>
                                    <p className="text-xs text-gray-500">Export all records recorded</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-green shrink-0" />
                        </button>
                    )}

                    {scoped && (
                        <div className="mt-6 pt-5 border-t border-gray-200">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                                Download as
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {formatList.map((fmt) => (
                                    <button
                                        key={fmt}
                                        type="button"
                                        disabled={formatButtonsDisabled}
                                        onClick={() => handleFormatClick(fmt)}
                                        className={formatButtonClass(fmt)}
                                    >
                                        {formatButtonLabel(fmt)}
                                    </button>
                                ))}
                            </div>
                            {selectedScope === 'dateRange' && (!exportStartDate || !exportEndDate) && (
                                <p className="mt-2 text-xs text-amber-700">
                                    Choose start and end dates to enable download.
                                </p>
                            )}
                            {!selectedScope && (
                                <p className="mt-2 text-xs text-gray-500">
                                    Select an export option above, then pick a file format.
                                </p>
                            )}
                        </div>
                    )}

                    {/* ——— Legacy: one-click export ——— */}
                    {!scoped && onExportCurrentPage && (
                        <button
                            onClick={onExportCurrentPage}
                            disabled={loading}
                            className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-xl hover:border-agrosaf-green hover:bg-green-50 transition-all group disabled:opacity-50"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200">
                                    <FileText className="h-5 w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Export Current Page</p>
                                    <p className="text-xs text-gray-500">Only data visible on this page</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-green" />
                        </button>
                    )}

                    {!scoped && onExportFiltered && (
                        <button
                            onClick={onExportFiltered}
                            disabled={loading}
                            className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-xl hover:border-agrosaf-blue hover:bg-blue-50 transition-all group disabled:opacity-50"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200">
                                    <Download className="h-5 w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Export Filtered</p>
                                    <p className="text-xs text-gray-500">All data matching current filters</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-blue" />
                        </button>
                    )}

                    {!scoped && showDateRange && onExportRange && (
                        <div className="p-4 border-2 border-gray-300 rounded-xl space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                                    <Calendar className="h-5 w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Export Date Range</p>
                                    <p className="text-xs text-gray-500">Select range carefully</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1 ml-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={exportStartDate}
                                        onChange={(e) => setExportStartDate(e.target.value)}
                                        onClick={(e) => e.target.showPicker()}
                                        max={
                                            allowFutureExportDates
                                                ? undefined
                                                : new Date().toISOString().split("T")[0]
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1 ml-1">End Date</label>
                                    <input
                                        type="date"
                                        value={exportEndDate}
                                        onChange={(e) => setExportEndDate(e.target.value)}
                                        onClick={(e) => e.target.showPicker()}
                                        min={exportStartDate}
                                        max={
                                            allowFutureExportDates
                                                ? undefined
                                                : new Date().toISOString().split("T")[0]
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-transparent outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <Button
                                onClick={handleLegacyExportRange}
                                disabled={loading || !exportStartDate || !exportEndDate}
                                fullWidth
                                variant="primary"
                                className="bg-orange-500 hover:bg-orange-600 border-none py-2.5 shadow-sm"
                            >
                                Download Range
                            </Button>
                        </div>
                    )}

                    {!scoped && onExportAll && (
                        <button
                            onClick={onExportAll}
                            disabled={loading}
                            className="w-full flex items-center justify-between p-4 border-2 border-gray-300 rounded-xl hover:border-agrosaf-green hover:bg-green-50 transition-all group disabled:opacity-50"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-2 bg-green-100 text-green-600 rounded-lg group-hover:bg-green-200">
                                    <Download className="h-5 w-5" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Export All Data</p>
                                    <p className="text-xs text-gray-500">Export all records recorded</p>
                                </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-agrosaf-green" />
                        </button>
                    )}
                </div>

                <div className="mt-8 flex justify-end">
                    <Button variant="secondary" onClick={onClose} disabled={loading} className="px-6 font-medium">
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
